import React, { useEffect, useMemo, useState } from 'react';
import { Layout } from '../components/Layout';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Plus,
  Handshake,
  Pencil,
  Trash2,
  ExternalLink,
  Copy,
  Check,
  Users,
  UserCircle,
  Loader2,
  Sparkles,
  MessageSquare,
  Megaphone,
} from 'lucide-react';
import { toast } from 'sonner';
import { usePermissions } from '../hooks/usePermissions';
import { useAuth } from '../contexts/AuthContext';
import { ImageUpload } from '../components/ImageUpload';
import { DobradaDetailDrawer } from '../components/DobradaDetailDrawer';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { ESTADOS } from '../components/StateCitySelect';
import cidadesBrasil from '../data/cidades-brasil.json';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  ativo: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  pausado: 'bg-amber-100 text-amber-700 border-amber-200',
  arquivado: 'bg-slate-100 text-slate-600 border-slate-200',
};

const emptyForm = {
  // Identidade
  nome: '',
  cargo: '',
  estado: '',
  cidades: [],
  numero: '',
  partido: '',
  primary_color: '',
  status: 'ativo',
  observacoes: '',
  default_destination_id: '',
  // Prévia WhatsApp (OG)
  og_image_url: '',
  og_title: '',
  og_description: '',
  // Apoiadores (CapturePage)
  hero_supporter_url: '',
  capture_subtitle: '',
  // Lideranças (LeaderSignupPage)
  hero_leader_url: '',
  signup_title: '',
  signup_subtitle: '',
};

export const DobradasPage = () => {
  const { canEdit } = usePermissions();
  const { activeTenant } = useAuth();
  const tenantSlug = activeTenant?.slug;
  const allowEdit = canEdit('dobradas');

  const [dobradas, setDobradas] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [detailDobrada, setDetailDobrada] = useState(null);

  useEffect(() => {
    fetchDobradas();
    fetchDestinations();
  }, []);

  const fetchDobradas = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/dobradas`, { withCredentials: true });
      setDobradas(data);
    } catch (err) {
      toast.error('Erro ao carregar dobradas');
    } finally {
      setLoading(false);
    }
  };

  const fetchDestinations = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/destinations`, {
        withCredentials: true,
      });
      setDestinations((data || []).filter((d) => d.status === 'ativo'));
    } catch {
      /* destinations require permission — fail silently for restricted users */
    }
  };

  const openNewDialog = () => {
    setEditingId(null);
    setFormData(emptyForm);
    setDialogOpen(true);
  };

  const openEditDialog = (d) => {
    setEditingId(d.id);
    setFormData({
      nome: d.nome || '',
      cargo: d.cargo || '',
      estado: d.estado || '',
      cidades: Array.isArray(d.cidades) ? d.cidades : [],
      numero: d.numero || '',
      partido: d.partido || '',
      primary_color: d.primary_color || '',
      status: d.status || 'ativo',
      observacoes: d.observacoes || '',
      default_destination_id: d.default_destination_id || '',
      og_image_url: d.og_image_url || '',
      og_title: d.og_title || '',
      og_description: d.og_description || '',
      hero_supporter_url: d.hero_supporter_url || '',
      capture_subtitle: d.capture_subtitle || '',
      hero_leader_url: d.hero_leader_url || '',
      signup_title: d.signup_title || '',
      signup_subtitle: d.signup_subtitle || '',
    });
    setDialogOpen(true);
  };

  const submitForm = async (e) => {
    e.preventDefault();
    if (!formData.nome.trim() || !formData.cargo.trim()) {
      toast.error('Preencha nome e cargo da dobrada');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        cidades: (formData.cidades || []).filter(Boolean),
        primary_color: formData.primary_color || null,
        default_destination_id: formData.default_destination_id || null,
      };
      if (editingId) {
        await axios.put(`${BACKEND_URL}/api/dobradas/${editingId}`, payload, { withCredentials: true });
        toast.success('Dobrada atualizada');
      } else {
        await axios.post(`${BACKEND_URL}/api/dobradas`, payload, { withCredentials: true });
        toast.success('Dobrada criada');
      }
      setDialogOpen(false);
      setEditingId(null);
      setFormData(emptyForm);
      fetchDobradas();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Erro ao salvar dobrada');
    } finally {
      setSubmitting(false);
    }
  };

  const removeDobrada = async (d) => {
    const confirm = window.confirm(
      `Excluir dobrada "${d.nome}"?\nAs ${d.stats?.liderancas_count || 0} lideranças vinculadas voltarão para o candidato principal.`
    );
    if (!confirm) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/dobradas/${d.id}`, { withCredentials: true });
      toast.success('Dobrada removida');
      fetchDobradas();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Erro ao remover dobrada');
    }
  };

  const publicUrlOf = (slug) =>
    tenantSlug ? `${window.location.origin}/${tenantSlug}/d/${slug}/cadastrar` : '';

  const copyPublicUrl = async (d) => {
    const url = publicUrlOf(d.slug);
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(d.id);
      toast.success('Link da dobrada copiado!');
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      toast.error('Não foi possível copiar');
    }
  };

  const cidadesDisponiveis = useMemo(() => {
    if (!formData.estado) return [];
    return cidadesBrasil[formData.estado] || [];
  }, [formData.estado]);

  const toggleCidade = (cidade) => {
    setFormData((prev) => {
      const current = new Set(prev.cidades || []);
      if (current.has(cidade)) current.delete(cidade);
      else current.add(cidade);
      return { ...prev, cidades: Array.from(current) };
    });
  };

  const kpis = useMemo(
    () => ({
      total: dobradas.length,
      ativas: dobradas.filter((d) => d.status === 'ativo').length,
      liderancas: dobradas.reduce((acc, d) => acc + (d.stats?.liderancas_count || 0), 0),
      apoiadores: dobradas.reduce((acc, d) => acc + (d.stats?.apoiadores_count || 0), 0),
    }),
    [dobradas]
  );

  return (
    <Layout>
      <div className="space-y-6" data-testid="dobradas-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-4xl font-black tracking-tight flex items-center gap-3">
              <Handshake className="h-8 w-8 text-violet-600" />
              Dobradas
            </h1>
            <p className="text-sm text-slate-500 mt-1 max-w-2xl">
              Candidatos parceiros aos quais o candidato principal está aliado. Cada dobrada agrupa lideranças,
              links e apoiadores em uma árvore própria — com banners e cores personalizados.
            </p>
          </div>
          <Button
            onClick={openNewDialog}
            disabled={!allowEdit}
            data-testid="create-dobrada-btn"
            className="bg-violet-600 hover:bg-violet-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nova Dobrada
          </Button>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi label="Dobradas" value={kpis.total} testid="kpi-total" />
          <Kpi label="Ativas" value={kpis.ativas} color="text-emerald-600" testid="kpi-ativas" />
          <Kpi label="Lideranças" value={kpis.liderancas} color="text-violet-600" testid="kpi-leaders" />
          <Kpi label="Apoiadores" value={kpis.apoiadores} color="text-blue-600" testid="kpi-supporters" />
        </div>

        {/* Lista */}
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
          </div>
        ) : dobradas.length === 0 ? (
          <EmptyState onCreate={openNewDialog} canCreate={allowEdit} />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {dobradas.map((d) => (
              <DobradaCard
                key={d.id}
                dobrada={d}
                publicUrl={publicUrlOf(d.slug)}
                copied={copiedId === d.id}
                onCopy={() => copyPublicUrl(d)}
                onEdit={() => openEditDialog(d)}
                onRemove={() => removeDobrada(d)}
                onOpen={() => setDetailDobrada(d)}
                allowEdit={allowEdit}
              />
            ))}
          </div>
        )}
      </div>

      {/* Dialog Create/Edit */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="dobrada-dialog">
          <DialogHeader>
            <DialogTitle>
              {editingId ? 'Editar dobrada' : 'Nova dobrada'}
            </DialogTitle>
            <DialogDescription>
              Cadastre um candidato parceiro. As lideranças vinculadas a esta dobrada herdarão automaticamente
              os banners, cor e textos definidos aqui.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitForm} className="space-y-4 pt-2">
            <Tabs defaultValue="identity" className="w-full">
              <TabsList className="grid grid-cols-4 w-full">
                <TabsTrigger value="identity" data-testid="dobrada-tab-identity">
                  Identidade
                </TabsTrigger>
                <TabsTrigger value="whatsapp" data-testid="dobrada-tab-whatsapp">
                  <Sparkles className="h-3.5 w-3.5 mr-1.5" />
                  Prévia WhatsApp
                </TabsTrigger>
                <TabsTrigger value="apoiadores" data-testid="dobrada-tab-apoiadores">
                  <Megaphone className="h-3.5 w-3.5 mr-1.5" />
                  Apoiadores
                </TabsTrigger>
                <TabsTrigger value="liderancas" data-testid="dobrada-tab-liderancas">
                  <Users className="h-3.5 w-3.5 mr-1.5" />
                  Lideranças
                </TabsTrigger>
              </TabsList>

              {/* ===== Identidade ===== */}
              <TabsContent value="identity" className="space-y-4 pt-4">
                <p className="text-xs text-slate-500 -mt-1">
                  Dados básicos do candidato parceiro e onde ele atua.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="d-nome">Nome do candidato parceiro *</Label>
                    <Input
                      id="d-nome"
                      value={formData.nome}
                      onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                      placeholder="Ex: Tia Eulália"
                      required
                      data-testid="dobrada-nome-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="d-cargo">Cargo em disputa *</Label>
                    <Input
                      id="d-cargo"
                      value={formData.cargo}
                      onChange={(e) => setFormData({ ...formData, cargo: e.target.value })}
                      placeholder="Ex: Deputada Federal"
                      required
                      data-testid="dobrada-cargo-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="d-numero">Número de urna</Label>
                    <Input
                      id="d-numero"
                      value={formData.numero}
                      onChange={(e) => setFormData({ ...formData, numero: e.target.value })}
                      placeholder="Ex: 13456"
                      inputMode="numeric"
                      data-testid="dobrada-numero-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="d-partido">Partido</Label>
                    <Input
                      id="d-partido"
                      value={formData.partido}
                      onChange={(e) => setFormData({ ...formData, partido: e.target.value })}
                      placeholder="Ex: PARTIDO 13"
                      data-testid="dobrada-partido-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="d-estado">Estado de atuação</Label>
                    <select
                      id="d-estado"
                      value={formData.estado}
                      onChange={(e) =>
                        setFormData({ ...formData, estado: e.target.value, cidades: [] })
                      }
                      className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
                      data-testid="dobrada-estado-select"
                    >
                      <option value="">UF</option>
                      {ESTADOS.map((e) => (
                        <option key={e.sigla} value={e.sigla}>
                          {e.sigla} — {e.nome}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label>Cidades de atuação (opcional)</Label>
                    <div className="border border-slate-200 rounded-md bg-white p-2 max-h-40 overflow-y-auto text-sm">
                      {!formData.estado ? (
                        <p className="text-xs text-slate-500 py-2 text-center">
                          Escolha um estado para listar as cidades.
                        </p>
                      ) : cidadesDisponiveis.length === 0 ? (
                        <p className="text-xs text-slate-500 py-2 text-center">
                          Sem cidades disponíveis para este estado.
                        </p>
                      ) : (
                        <div className="flex flex-wrap gap-1.5">
                          {cidadesDisponiveis.slice(0, 200).map((c) => {
                            const selected = (formData.cidades || []).includes(c);
                            return (
                              <button
                                type="button"
                                key={c}
                                onClick={() => toggleCidade(c)}
                                className={`text-xs px-2 py-1 rounded-md border transition-colors ${
                                  selected
                                    ? 'bg-violet-600 text-white border-violet-600'
                                    : 'bg-white text-slate-700 border-slate-200 hover:border-violet-300'
                                }`}
                              >
                                {c}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="d-status">Status</Label>
                    <select
                      id="d-status"
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
                      data-testid="dobrada-status-select"
                    >
                      <option value="ativo">Ativo</option>
                      <option value="pausado">Pausado</option>
                      <option value="arquivado">Arquivado</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="d-color">Cor de destaque (opcional)</Label>
                    <div className="flex gap-2">
                      <Input
                        id="d-color"
                        type="color"
                        value={formData.primary_color || '#16a34a'}
                        onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                        className="h-10 w-16 p-1 cursor-pointer"
                        data-testid="dobrada-color-input"
                      />
                      <Input
                        value={formData.primary_color}
                        onChange={(e) => setFormData({ ...formData, primary_color: e.target.value })}
                        placeholder="#16a34a (em branco usa a cor do tenant)"
                        className="font-mono text-xs"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="d-default-dest">Destino padrão da dobrada</Label>
                    <select
                      id="d-default-dest"
                      value={formData.default_destination_id || ''}
                      onChange={(e) =>
                        setFormData({ ...formData, default_destination_id: e.target.value })
                      }
                      className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
                      data-testid="dobrada-default-destination-select"
                    >
                      <option value="">— Usar destino padrão do tenant —</option>
                      {destinations.map((d) => (
                        <option key={d.id} value={d.id}>
                          {d.nome} {d.tipo ? `(${d.tipo})` : ''}
                        </option>
                      ))}
                    </select>
                    <p className="text-[10px] text-slate-500">
                      Quando definido, os links auto-gerados para lideranças desta dobrada (via cadastro
                      público ou indicação) usam este destino em vez do padrão geral.
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="d-obs">Observações internas</Label>
                  <Textarea
                    id="d-obs"
                    value={formData.observacoes}
                    onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                    rows={2}
                    data-testid="dobrada-observacoes-input"
                  />
                </div>
              </TabsContent>

              {/* ===== Prévia WhatsApp (OG) ===== */}
              <TabsContent value="whatsapp" className="space-y-4 pt-4">
                <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2 text-xs text-emerald-900">
                  Sobrescreve a prévia que aparece quando alguém compartilha um link
                  desta dobrada no WhatsApp, Telegram, etc. Se a liderança ou o link
                  específico tiverem suas próprias prévias, essas terão prioridade.
                </div>
                <div className="space-y-2">
                  <Label>Imagem de destaque (1200×628)</Label>
                  <ImageUpload
                    value={formData.og_image_url}
                    onChange={(url) => setFormData({ ...formData, og_image_url: url })}
                    folder={`meadd/tenants/${activeTenant?.id || 'default'}/dobradas`}
                    label="Imagem da prévia"
                    aspect="aspect-[1200/628]"
                    testId="dobrada-og-image-upload"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="d-og-title">Título</Label>
                  <Input
                    id="d-og-title"
                    value={formData.og_title}
                    onChange={(e) => setFormData({ ...formData, og_title: e.target.value })}
                    placeholder={`Ex.: ${formData.nome || 'Tia Eulália'} te convidou para a campanha`}
                    maxLength={120}
                    data-testid="dobrada-og-title-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="d-og-desc">Descrição</Label>
                  <Textarea
                    id="d-og-desc"
                    value={formData.og_description}
                    onChange={(e) => setFormData({ ...formData, og_description: e.target.value })}
                    placeholder="Toque para entrar na rede no WhatsApp."
                    rows={3}
                    maxLength={240}
                    data-testid="dobrada-og-desc-input"
                  />
                  <p className="text-xs text-slate-500 text-right">
                    {(formData.og_description || '').length}/240
                  </p>
                </div>
              </TabsContent>

              {/* ===== Apoiadores ===== */}
              <TabsContent value="apoiadores" className="space-y-4 pt-4">
                <div className="rounded-lg bg-blue-50 border border-blue-200 px-3 py-2 text-xs text-blue-900">
                  Personaliza a página de captação de apoiadores (CapturePage) dos
                  links derivados desta dobrada. Se a liderança ou o link tiver
                  personalização própria, essas terão prioridade.
                </div>
                <div className="space-y-2">
                  <Label>Imagem do cabeçalho (1200×628)</Label>
                  <ImageUpload
                    value={formData.hero_supporter_url}
                    onChange={(url) => setFormData({ ...formData, hero_supporter_url: url })}
                    folder={`meadd/tenants/${activeTenant?.id || 'default'}/dobradas`}
                    label="Banner apoiadores"
                    aspect="aspect-[1200/628]"
                    testId="dobrada-hero-supporter-upload"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="d-subtitle">Mensagem antes dos campos</Label>
                  <Textarea
                    id="d-subtitle"
                    value={formData.capture_subtitle}
                    onChange={(e) => setFormData({ ...formData, capture_subtitle: e.target.value })}
                    placeholder="Texto que aparecerá acima do formulário de apoiadores."
                    rows={3}
                    maxLength={240}
                    data-testid="dobrada-subtitle-input"
                  />
                  <p className="text-xs text-slate-500 text-right">
                    {(formData.capture_subtitle || '').length}/240
                  </p>
                </div>
              </TabsContent>

              {/* ===== Lideranças ===== */}
              <TabsContent value="liderancas" className="space-y-4 pt-4">
                <div className="rounded-lg bg-violet-50 border border-violet-200 px-3 py-2 text-xs text-violet-900">
                  Personaliza a página pública de cadastro de lideranças desta dobrada
                  (<span className="font-mono">/{tenantSlug || 'tenant'}/d/{'<slug>'}/cadastrar</span>).
                  Se a liderança convidante tiver personalização própria, ela terá prioridade.
                </div>
                <div className="space-y-2">
                  <Label>Banner da página de lideranças (1200×628)</Label>
                  <ImageUpload
                    value={formData.hero_leader_url}
                    onChange={(url) => setFormData({ ...formData, hero_leader_url: url })}
                    folder={`meadd/tenants/${activeTenant?.id || 'default'}/dobradas`}
                    label="Banner lideranças"
                    aspect="aspect-[1200/628]"
                    testId="dobrada-hero-leader-upload"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="d-signup-title">Título</Label>
                  <Input
                    id="d-signup-title"
                    value={formData.signup_title}
                    onChange={(e) => setFormData({ ...formData, signup_title: e.target.value })}
                    placeholder={`Ex.: Faça parte da rede do(a) ${formData.nome || 'candidato parceiro'}`}
                    maxLength={120}
                    data-testid="dobrada-signup-title-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="d-signup-sub">Subtítulo</Label>
                  <Textarea
                    id="d-signup-sub"
                    value={formData.signup_subtitle}
                    onChange={(e) => setFormData({ ...formData, signup_subtitle: e.target.value })}
                    placeholder="Mensagem para quem foi indicado(a) por uma liderança desta dobrada."
                    rows={3}
                    maxLength={240}
                    data-testid="dobrada-signup-sub-input"
                  />
                  <p className="text-xs text-slate-500 text-right">
                    {(formData.signup_subtitle || '').length}/240
                  </p>
                </div>
              </TabsContent>
            </Tabs>

            <DialogFooter className="gap-2 pt-2 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
                data-testid="dobrada-cancel-btn"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-violet-600 hover:bg-violet-700"
                data-testid="dobrada-submit-btn"
              >
                {submitting ? 'Salvando...' : editingId ? 'Salvar alterações' : 'Criar dobrada'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <DobradaDetailDrawer
        dobrada={detailDobrada}
        open={!!detailDobrada}
        onOpenChange={(v) => !v && setDetailDobrada(null)}
        onRefresh={fetchDobradas}
      />
    </Layout>
  );
};

const Kpi = ({ label, value, color = 'text-slate-900', testid }) => (
  <Card>
    <CardContent className="py-4">
      <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-2xl font-black mt-1 ${color}`} data-testid={testid}>
        {value}
      </div>
    </CardContent>
  </Card>
);

const EmptyState = ({ onCreate, canCreate }) => (
  <Card>
    <CardContent className="py-12 text-center space-y-3">
      <div className="h-14 w-14 rounded-2xl bg-violet-100 text-violet-600 flex items-center justify-center mx-auto">
        <Handshake className="h-7 w-7" />
      </div>
      <h3 className="text-lg font-bold text-slate-900">Sem dobradas ainda</h3>
      <p className="text-sm text-slate-500 max-w-md mx-auto">
        Crie uma dobrada para agrupar as lideranças que apoiam um candidato parceiro. Cada dobrada
        gera um link público próprio de cadastro de lideranças.
      </p>
      {canCreate && (
        <Button onClick={onCreate} className="bg-violet-600 hover:bg-violet-700" data-testid="empty-create-btn">
          <Plus className="h-4 w-4 mr-2" />
          Criar primeira dobrada
        </Button>
      )}
    </CardContent>
  </Card>
);

const DobradaCard = ({ dobrada, publicUrl, copied, onCopy, onEdit, onRemove, onOpen, allowEdit }) => {
  const color = dobrada.primary_color || '#7c3aed';
  return (
    <Card
      className="overflow-hidden border-slate-200 hover:border-violet-300 transition-colors"
      data-testid={`dobrada-card-${dobrada.slug}`}
    >
      <button
        type="button"
        onClick={onOpen}
        className="w-full text-left block"
        data-testid={`open-dobrada-${dobrada.slug}`}
      >
        <div
          className="aspect-[1200/628] w-full relative bg-slate-100"
          style={{
            background: dobrada.hero_supporter_url
              ? `url(${dobrada.hero_supporter_url}) center/cover`
              : `linear-gradient(135deg, ${color}, ${color}99)`,
          }}
        >
          <span
            className={`absolute top-2 right-2 text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full border ${
              STATUS_STYLES[dobrada.status] || STATUS_STYLES.ativo
            }`}
          >
            {dobrada.status}
          </span>
        </div>
        <CardContent className="pt-4 space-y-3">
          <div>
            <h3 className="font-bold text-slate-900 truncate" title={dobrada.nome}>
              {dobrada.nome}
            </h3>
            <p className="text-xs text-slate-500 truncate">
              {dobrada.cargo}
              {dobrada.estado ? ` · ${dobrada.estado}` : ''}
              {dobrada.numero ? ` · nº ${dobrada.numero}` : ''}
            </p>
          </div>

          <div className="grid grid-cols-3 gap-2 text-center">
            <Stat
              icon={<Users className="h-3.5 w-3.5" />}
              value={dobrada.stats?.liderancas_count || 0}
              label="Líderes"
            />
            <Stat
              icon={<UserCircle className="h-3.5 w-3.5" />}
              value={dobrada.stats?.apoiadores_count || 0}
              label="Apoios"
            />
            <Stat
              icon={<ExternalLink className="h-3.5 w-3.5" />}
              value={dobrada.stats?.links_count || 0}
              label="Links"
            />
          </div>
        </CardContent>
      </button>
      <CardContent className="pt-0 space-y-3">
        {publicUrl && (
          <div className="flex gap-1.5">
            <Input
              value={publicUrl}
              readOnly
              className="font-mono text-[10px] h-8"
              data-testid={`public-url-${dobrada.slug}`}
              onClick={(e) => e.stopPropagation()}
            />
            <Button
              type="button"
              size="icon"
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                onCopy();
              }}
              className="h-8 w-8 shrink-0"
              data-testid={`copy-url-${dobrada.slug}`}
            >
              {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
            </Button>
            <Button
              type="button"
              size="icon"
              variant="outline"
              asChild
              className="h-8 w-8 shrink-0"
            >
              <a
                href={publicUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
              >
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            </Button>
          </div>
        )}

        <div className="flex gap-2 pt-1">
          <Button
            size="sm"
            variant="outline"
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
            disabled={!allowEdit}
            className="flex-1"
            data-testid={`edit-dobrada-${dobrada.slug}`}
          >
            <Pencil className="h-3.5 w-3.5 mr-1.5" />
            Editar
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            disabled={!allowEdit}
            className="text-rose-600 hover:text-rose-700 border-rose-200 hover:border-rose-300"
            data-testid={`delete-dobrada-${dobrada.slug}`}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const Stat = ({ icon, value, label }) => (
  <div className="bg-slate-50 rounded-md py-2 px-1">
    <div className="flex items-center justify-center gap-1 text-slate-500">{icon}</div>
    <div className="text-sm font-bold text-slate-900">{value}</div>
    <div className="text-[10px] uppercase text-slate-500">{label}</div>
  </div>
);
